// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { Button, Group, Stack } from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import { IconPlus, IconCheck, IconX } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { FilterBar } from '../../components/FilterBar';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useRequests, useApproveRequest, useDenyRequest } from '../../hooks/useRequests';
import { useAuth } from '@ferp/auth';

interface Request {
  id: string;
  employeeId: string;
  requestType: string;
  status: string;
  dateStart: string;
  dateEnd: string;
  message: string;
}

export function RequestListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManagerOrAdmin = user?.roles?.some(r => r.name === 'admin') || user?.roles?.some(r => r.name === 'manager') || user?.roles?.some(r => r.name === 'payroll_admin');

  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const [confirmAction, setConfirmAction] = useState<{ type: 'approve' | 'deny'; id: string } | null>(null);

  const { data: queryData, loading, refetch } = useRequests({
    page,
    perPage: pageSize,
    status: filterValues.status || undefined,
  });
  const requestsData = queryData as { requests?: { data: Request[]; total: number } } | undefined;
  const data = requestsData?.requests?.data ?? [];
  const total = requestsData?.requests?.total ?? 0;

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const approveHook = useApproveRequest();
  const denyHook = useDenyRequest();

  const handleAction = async () => {
    if (!confirmAction) return;
    try {
      const mutationFn = confirmAction.type === 'approve' ? approveHook : denyHook;
      await mutationFn.mutateAsync({ id: confirmAction.id });
      notifications.show({
        title: 'Success',
        message: `Request ${confirmAction.type === 'approve' ? 'approved' : 'denied'}`,
        color: 'green',
      });
      setConfirmAction(null);
      refetch();
    } catch (err) {
      notifications.show({ title: 'Error', message: `Failed to ${confirmAction.type} request`, color: 'red' });
    }
  };

  const formatType = (type: string) =>
    type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const columns: Column<Request>[] = [
    {
      key: 'employeeId',
      label: 'Employee',
      sortable: true,
    },
    {
      key: 'requestType',
      label: 'Request Type',
      render: (row) => formatType(row.requestType),
    },
    {
      key: 'dateStart',
      label: 'Start Date',
      sortable: true,
      render: (row) => new Date(row.dateStart).toLocaleDateString(),
    },
    {
      key: 'dateEnd',
      label: 'End Date',
      render: (row) => new Date(row.dateEnd).toLocaleDateString(),
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'message',
      label: 'Message',
      render: (row) => row.message?.length > 50 ? `${row.message.slice(0, 50)}...` : row.message || '-',
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Requests"
        subtitle={`${total} requests`}
        actions={
          <Button
            icon={<IconPlus size={16} />}
            onClick={() => navigate('/admin/workforce/leave/requests/new')}
          >
            New Request
          </Button>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={(val) => {
          setSearch(val);
          setPage(1);
        }}
        searchPlaceholder="Search requests..."
        filters={[
          {
            key: 'type',
            label: 'Type',
            options: [
              { value: 'timeOff', label: 'Time Off' },
              { value: 'schedule', label: 'Schedule' },
              { value: 'absence', label: 'Absence' },
              { value: 'overtime', label: 'Overtime' },
            ],
          },
          {
            key: 'status',
            label: 'Status',
            options: [
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'denied', label: 'Denied' },
              { value: 'cancelled', label: 'Cancelled' },
            ],
          },
        ]}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
      />

      <DataTable<Record<string, unknown>>
        columns={columns as unknown as Column<Record<string, unknown>>[]}
        data={data as unknown as Record<string, unknown>[]}
        loading={loading}
        pagination={{
          page,
          pageSize,
          total,
          onPageChange: setPage,
        }}
        getRowKey={(row) => row.id as string}
        actions={
          isManagerOrAdmin
            ? (row) => {
                const request = row as unknown as Request;
                if (request.status !== 'pending') return null;
                return (
                  <Group gap="xs">
                    <Button
                      size="compact-xs"
                      color="green"
                      variant="light"
                      icon={<IconCheck size={14} />}
                      onClick={() => setConfirmAction({ type: 'approve', id: request.id })}
                    >
                      Approve
                    </Button>
                    <Button
                      size="compact-xs"
                      color="red"
                      variant="light"
                      icon={<IconX size={14} />}
                      onClick={() => setConfirmAction({ type: 'deny', id: request.id })}
                    >
                      Deny
                    </Button>
                  </Group>
                );
              }
            : undefined
        }
      />

      <ConfirmModal
        opened={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleAction}
        title={confirmAction?.type === 'approve' ? 'Approve Request' : 'Deny Request'}
        message={`Are you sure you want to ${confirmAction?.type} this request?`}
        confirmLabel={confirmAction?.type === 'approve' ? 'Approve' : 'Deny'}
        color={confirmAction?.type === 'approve' ? 'green' : 'red'}
      />
    </Stack>
  );
}

export default RequestListPage;
